import React, { useState, useEffect } from 'react';
import { apiPost, apiGet } from '../utils/api';
import { Palette, Ruler, DollarSign, Copy, RefreshCw, Thermometer } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';
import TemperatureConverter from './TemperatureConverter';

// Função utilitária para obter categoria e nome da ferramenta ativa
export function getActiveToolInfo(location) {
  const tabs = [
    { id: 'colors', name: 'Cores', path: '/converters/colors' },
    { id: 'units', name: 'Unidades', path: '/converters/units' },
    { id: 'currency', name: 'Moedas', path: '/converters/currency' },
    { id: 'time', name: 'Tempo', path: '/converters/time' },
    { id: 'temperature', name: 'Temperatura', path: '/converters/temperature' }
  ];
  const match = tabs.find(tab => location.pathname.startsWith(tab.path));
  return { category: 'Conversores', tool: match ? match.name : null };
}

const Placeholder = ({ title }) => (
  <div className="bg-white rounded-lg shadow-lg p-6 text-center text-gray-500 text-xl">
    {title} - Em breve
  </div>
);

const Converters = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Definição das abas e rotas
  const tabs = [
    { id: 'colors', name: 'Cores', icon: Palette, path: '/converters/colors', description: 'Conversor de cores', status: 'active' },
    { id: 'units', name: 'Unidades', icon: Ruler, path: '/converters/units', description: 'Conversor de unidades', status: 'active' },
    { id: 'currency', name: 'Moedas', icon: DollarSign, path: '/converters/currency', description: 'Conversor de moedas', status: 'active' },
    { id: 'time', name: 'Tempo', icon: RefreshCw, path: '/converters/time', description: 'Conversor de tempo', status: 'active' },
    { id: 'temperature', name: 'Temperatura', icon: Thermometer, path: '/converters/temperature', description: 'Conversor de temperatura', status: 'active' }
  ];

  // Detectar aba ativa pela URL
  const getActiveTab = () => {
    const match = tabs.find(tab => location.pathname.startsWith(tab.path));
    return match ? match.id : 'colors';
  };
  const activeTab = getActiveTab();

  // Redirecionar /converters para /converters/colors
  useEffect(() => {
    if (location.pathname === '/converters' || location.pathname === '/converters/') {
      navigate('/converters/colors', { replace: true });
    }
  }, [location.pathname, navigate]);

  const [hexColor, setHexColor] = useState('#3B82F6');
  const [rgbColor, setRgbColor] = useState({ r: 59, g: 130, b: 246 });
  const [hslColor, setHslColor] = useState({ h: 217, s: 91, l: 60 });
  const [unitType, setUnitType] = useState('length');
  const [fromUnit, setFromUnit] = useState('meter');
  const [toUnit, setToUnit] = useState('feet');
  const [unitValue, setUnitValue] = useState(1);
  const [unitResult, setUnitResult] = useState(0);
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [currencyValue, setCurrencyValue] = useState(1);
  const [currencyResult, setCurrencyResult] = useState(0);
  const [exchangeRates, setExchangeRates] = useState({});
  const [editingColor, setEditingColor] = useState(null); // 'hex', 'rgb', 'hsl' ou null
  const [timeValue, setTimeValue] = useState('');
  const [timeFrom, setTimeFrom] = useState('seconds');
  const [timeTo, setTimeTo] = useState('minutes');
  const [timeResult, setTimeResult] = useState('');
  const timeUnits = [
    { value: 'seconds', label: 'Segundos' },
    { value: 'minutes', label: 'Minutos' },
    { value: 'hours', label: 'Horas' },
    { value: 'days', label: 'Dias' }
  ];

  // Conversão de cores
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const hslToRgb = (h, s, l) => {
    h /= 360;
    s /= 100;
    l /= 100;

    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  };

  // Função para validar HEX (3 ou 6 dígitos)
  const isValidHex = (hex) => {
    const clean = hex.replace('#', '');
    return /^([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/.test(clean);
  };

  // Função para expandir HEX curto para longo
  const expandHex = (hex) => {
    let clean = hex.replace('#', '');
    if (clean.length === 3) {
      clean = clean.split('').map(c => c + c).join('');
    }
    return '#' + clean;
  };

  // Função central para atualizar todas as cores
  const updateColors = (type, value, blurOrEnter = false) => {
    if (editingColor && !blurOrEnter && editingColor === type) {
      if (type === 'hex') setHexColor(value);
      if (type === 'rgb') setRgbColor(value);
      if (type === 'hsl') setHslColor(value);
      return;
    }
    if (type === 'hex') {
      setHexColor(value);
      if (blurOrEnter && isValidHex(value)) {
        const expanded = expandHex(value);
        const rgb = hexToRgb(expanded);
        if (rgb) {
          setHexColor(expanded);
          setRgbColor(rgb);
          setHslColor(rgbToHsl(rgb.r, rgb.g, rgb.b));
        }
      }
    } else if (type === 'rgb') {
      setRgbColor(value);
      if (blurOrEnter && Object.values(value).every(v => !isNaN(v) && v >= 0 && v <= 255)) {
        const hex = rgbToHex(value.r, value.g, value.b);
        setHexColor(hex);
        setHslColor(rgbToHsl(value.r, value.g, value.b));
      }
    } else if (type === 'hsl') {
      setHslColor(value);
      if (blurOrEnter && !isNaN(value.h) && value.h >= 0 && value.h <= 360 && !isNaN(value.s) && value.s >= 0 && value.s <= 100 && !isNaN(value.l) && value.l >= 0 && value.l <= 100) {
        const rgb = hslToRgb(value.h, value.s, value.l);
        setRgbColor(rgb);
        setHexColor(rgbToHex(rgb.r, rgb.g, rgb.b));
      }
    }
  };

  // Conversão de unidades
  const unitConversions = {
    length: {
      meter: 1,
      kilometer: 1000,
      centimeter: 0.01,
      millimeter: 0.001,
      mile: 1609.344,
      yard: 0.9144,
      feet: 0.3048,
      inch: 0.0254
    },
    weight: {
      kilogram: 1,
      gram: 0.001,
      pound: 0.45359237,
      ounce: 0.028349523125,
      ton: 1000
    },
    volume: {
      liter: 1,
      milliliter: 0.001,
      gallon: 3.78541,
      quart: 0.946353,
      pint: 0.473176,
      cup: 0.236588
    },
    temperature: {
      celsius: 'celsius',
      fahrenheit: 'fahrenheit',
      kelvin: 'kelvin'
    }
  };

  const convertUnit = () => {
    if (unitType === 'temperature') {
      let result;
      const value = parseFloat(unitValue);
      
      if (fromUnit === 'celsius') {
        if (toUnit === 'fahrenheit') result = (value * 9/5) + 32;
        else if (toUnit === 'kelvin') result = value + 273.15;
        else result = value;
      } else if (fromUnit === 'fahrenheit') {
        if (toUnit === 'celsius') result = (value - 32) * 5/9;
        else if (toUnit === 'kelvin') result = (value - 32) * 5/9 + 273.15;
        else result = value;
      } else if (fromUnit === 'kelvin') {
        if (toUnit === 'celsius') result = value - 273.15;
        else if (toUnit === 'fahrenheit') result = (value - 273.15) * 9/5 + 32;
        else result = value;
      }
      
      setUnitResult(result);
    } else {
      const fromFactor = unitConversions[unitType][fromUnit];
      const toFactor = unitConversions[unitType][toUnit];
      const result = (parseFloat(unitValue) * fromFactor) / toFactor;
      setUnitResult(result);
    }
  };

  useEffect(() => {
    convertUnit();
  }, [unitValue, fromUnit, toUnit, unitType]);

  // Conversão de moedas
  const fetchExchangeRates = async () => {
    try {
      const data = await apiPost('/api/converters/currency-converter', {
        amount: parseFloat(currencyValue) || 1, 
        fromCurrency, 
        toCurrency 
      });
      setCurrencyResult(Number(data.result));
    } catch (error) {
      toast.error('Erro ao converter moeda');
    }
  };

  useEffect(() => {
    if (currencyValue && fromCurrency && toCurrency) {
      fetchExchangeRates();
    }
  }, [currencyValue, fromCurrency, toCurrency]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  const convertTime = () => {
    const value = parseFloat(timeValue);
    if (isNaN(value)) {
      setTimeResult('Valor inválido');
      return;
    }
    let seconds = value;
    if (timeFrom === 'minutes') seconds *= 60;
    if (timeFrom === 'hours') seconds *= 3600;
    if (timeFrom === 'days') seconds *= 86400;
    let result = seconds;
    if (timeTo === 'minutes') result = seconds / 60;
    if (timeTo === 'hours') result = seconds / 3600;
    if (timeTo === 'days') result = seconds / 86400;
    setTimeResult(result);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Conversores
        </h1>
        <p className="text-gray-600">
          Conversores de cores, unidades e moedas
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="colors" element={(
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
                {/* HEX */}
                <div className="space-y-3 flex flex-col h-full">
                  <label className="block text-sm font-medium text-gray-900">
                    HEX
                  </label>
                  <div className="flex items-center space-x-2 w-full">
                    <div
                      className="w-8 h-8 rounded border border-gray-300 flex-shrink-0"
                      style={{ backgroundColor: hexColor }}
                    />
                    <input
                      type="text"
                      value={hexColor}
                      onChange={e => {
                        setEditingColor('hex');
                        updateColors('hex', e.target.value);
                      }}
                      onBlur={e => {
                        setEditingColor(null);
                        updateColors('hex', e.target.value, true);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          setEditingColor(null);
                          updateColors('hex', e.target.value, true);
                        }
                      }}
                      className="max-w-[140px] w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => copyToClipboard(hexColor)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
                {/* RGB */}
                <div className="space-y-3 flex flex-col h-full">
                  <label className="block text-sm font-medium text-gray-900">
                    RGB
                  </label>
                  <div className="grid grid-cols-3 gap-2 w-full">
                    {['r', 'g', 'b'].map((channel) => (
                      <input
                        key={channel}
                        type="number"
                        min="0"
                        max="255"
                        value={rgbColor[channel]}
                        onChange={e => {
                          setEditingColor('rgb');
                          updateColors('rgb', { ...rgbColor, [channel]: parseInt(e.target.value) || 0 });
                        }}
                        onBlur={e => {
                          setEditingColor(null);
                          updateColors('rgb', { ...rgbColor, [channel]: parseInt(e.target.value) || 0 }, true);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setEditingColor(null);
                            updateColors('rgb', { ...rgbColor, [channel]: parseInt(e.target.value) || 0 }, true);
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                      />
                    ))}
                  </div>
                  <div className="text-sm text-gray-600 text-center">
                    rgb({rgbColor.r}, {rgbColor.g}, {rgbColor.b})
                  </div>
                </div>
                {/* HSL */}
                <div className="space-y-3 flex flex-col h-full">
                  <label className="block text-sm font-medium text-gray-900">
                    HSL
                  </label>
                  <div className="grid grid-cols-3 gap-2 w-full">
                    {['h', 's', 'l'].map((channel) => (
                      <input
                        key={channel}
                        type="number"
                        min={channel === 'h' ? 0 : 0}
                        max={channel === 'h' ? 360 : 100}
                        value={hslColor[channel]}
                        onChange={e => {
                          setEditingColor('hsl');
                          updateColors('hsl', { ...hslColor, [channel]: parseInt(e.target.value) || 0 });
                        }}
                        onBlur={e => {
                          setEditingColor(null);
                          updateColors('hsl', { ...hslColor, [channel]: parseInt(e.target.value) || 0 }, true);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setEditingColor(null);
                            updateColors('hsl', { ...hslColor, [channel]: parseInt(e.target.value) || 0 }, true);
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                      />
                    ))}
                  </div>
                  <div className="text-sm text-gray-600 text-center">
                    hsl({hslColor.h}°, {hslColor.s}%, {hslColor.l}%)
                  </div>
                </div>
              </div>
              {/* Preview */}
              <div className="bg-gray-50 p-4 rounded-lg mt-8">
                <div className="text-center">
                  <div
                    className="w-32 h-32 mx-auto rounded-lg border border-gray-300 mb-4"
                    style={{ backgroundColor: hexColor }}
                  />
                  <p className="text-sm text-gray-600">
                    Preview da cor
                  </p>
                </div>
              </div>
            </div>
          </div>
        )} />
        <Route path="units" element={(
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Tipo de Unidade
                </label>
                <select
                  value={unitType}
                  onChange={(e) => setUnitType(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="length">Comprimento</option>
                  <option value="weight">Peso</option>
                  <option value="volume">Volume</option>
                  <option value="temperature">Temperatura</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    De
                  </label>
                  <select
                    value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.keys(unitConversions[unitType]).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Valor
                  </label>
                  <input
                    type="number"
                    value={unitValue}
                    onChange={(e) => setUnitValue(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Para
                  </label>
                  <select
                    value={toUnit}
                    onChange={(e) => setToUnit(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.keys(unitConversions[unitType]).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-2">
                    {unitResult.toFixed(4)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {unitValue} {fromUnit} = {unitResult.toFixed(4)} {toUnit}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} />
        <Route path="currency" element={(
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Conversor de Moedas
                </h3>
                <button
                  onClick={fetchExchangeRates}
                  className="flex items-center space-x-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <RefreshCw size={14} />
                  <span>Atualizar</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    De
                  </label>
                  <select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="USD">USD - Dólar Americano</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="BRL">BRL - Real Brasileiro</option>
                    <option value="GBP">GBP - Libra Esterlina</option>
                    <option value="JPY">JPY - Iene Japonês</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Valor
                  </label>
                  <input
                    type="number"
                    value={currencyValue}
                    onChange={(e) => setCurrencyValue(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Para
                  </label>
                  <select
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - Dólar Americano</option>
                    <option value="BRL">BRL - Real Brasileiro</option>
                    <option value="GBP">GBP - Libra Esterlina</option>
                    <option value="JPY">JPY - Iene Japonês</option>
                  </select>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-2">
                    {typeof currencyResult === 'number' && !isNaN(currencyResult)
                      ? currencyResult.toFixed(2)
                      : '—'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {currencyValue} {fromCurrency} = {typeof currencyResult === 'number' && !isNaN(currencyResult)
                      ? currencyResult.toFixed(2)
                      : '—'} {toCurrency}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} />
        <Route path="time" element={(
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
            <div className="flex flex-col space-y-4">
              <div className="flex space-x-2 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-900 mb-1">Valor</label>
                  <input type="number" value={timeValue} onChange={e => setTimeValue(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">De</label>
                  <select value={timeFrom} onChange={e => setTimeFrom(e.target.value)} className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    {timeUnits.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Para</label>
                  <select value={timeTo} onChange={e => setTimeTo(e.target.value)} className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    {timeUnits.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <button onClick={convertTime} className="ml-2 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200">Converter</button>
              </div>
              {timeResult !== '' && (
                <div className="bg-gray-50 p-4 rounded-lg text-center text-lg font-mono text-gray-900">
                  {timeResult}
                </div>
              )}
            </div>
          </div>
        )} />
        <Route path="temperature" element={<TemperatureConverter />} />
      </Routes>
    </div>
  );
};

export default Converters; 